'use client'

import React, { memo, useCallback, useEffect, useRef } from 'react'
import { resolveSectionModel } from '@/shared/sectionFrames'
import {
	applyProductEnvMaterials,
	environmentUrl,
	resolveEnvironmentMap,
	setEnvironmentBaseUrl,
} from '@/shared/productViewer'
import { v4 as uuidv4 } from 'uuid'
import { ThreeViewer } from './ThreeViewer'
import { ItemProps } from '@/components/Form/types'

import Button from '../../UiElements/Button'
import ScrollToBottomButton from '@/components/UiElements/ScrollToBottomButton'
import loadProduct from '../utils/loadProduct'
import type { UvProjection } from '../utils/uvGeneration'
import initEnvironment from '../utils/initEnvironment'
import ButtonList from '@/components/UiElements/ListButton'
import SectionForm from './SectionForm'
import LightingForm from '@/components/ProductProfile/components/LightingForm'
import Splash from '@/components/splash/Splash'
import StickyDesignSection from './StickyDesignSection'

import updateAllTargetObjects from '@/components/ProductProfile/utils/drawSection/updateAllTargetObjects'
import cleanAllTargetObjects from '@/components/ProductProfile/utils/drawSection/cleanAllTargetObjects'
import animateTo from '@/components/ProductProfile/utils/animateTo'
import zoomToFrameSection from '@/components/ProductProfile/utils/zoomToFrameSection'



const Preview3dPlugin: React.FC<ItemProps> = (props) => {


	const { formData } = props

	const containerRef = useRef<HTMLDivElement>(null)
	const sectionFormScrollRef = useRef<HTMLDivElement>(null)

	const [file, setFile] = React.useState<string | null>(null)
	const [selectedSection, setSelectedSection] = React.useState<any | null>(null)
	const [sectionList, setSectionList] = React.useState<any[]>(formData.sections || [])
	const [lights, setLights] = React.useState<any>({})
	const [fullScreen, setFullScreen] = React.useState<boolean>(false)
	const [loaded, setLoaded] = React.useState(false)
	const [loading, setLoading] = React.useState(false)
	const [initialized, setInitialized] = React.useState(false)
	const [openForm, setOpenForm] = React.useState<'section' | 'lighting' | null>(null)

	const updateDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const debouncedUpdateAllTargetObjects = useCallback((drawingData: any[], sectionId: string) => {
		if (updateDebounceRef.current) clearTimeout(updateDebounceRef.current)
		updateDebounceRef.current = setTimeout(() => {
			updateAllTargetObjects(drawingData, sectionId)
		}, 100)
	}, [])

	const initThreeViewer = async () => {
		const container = containerRef.current
		if (container) {
			const threeViewer = new ThreeViewer({
				element: container,
				bgColor: '#f3f4f6',
			})
			await threeViewer.initViewer()
			// The profile's own environment, not a hard-coded one — see
			// shared/productViewer/environments. Absent resolves to what every profile used
			// before the setting existed, so nothing already saved changes.
			// Serve the maps from wherever VITE_HDR_BASE_URL points, falling back to this
			// app's own public folder. They are ~1.5 MB each and a profile uses one.
			setEnvironmentBaseUrl(import.meta.env.VITE_HDR_BASE_URL)
			const envMap = resolveEnvironmentMap(formData?.lights)
			;(window as any).threeViewer.__environmentMap = envMap
			await initEnvironment(threeViewer, environmentUrl(envMap));
			threeViewer.controls.smoothTime = 0.02;

			(window as any).threeViewer = threeViewer
			setInitialized(true)
		}

	}



	const loadProductIntoScene = async (base64: string) => {
		if (base64) {
			// Extract UV projection from view profile
			const uvProjection: UvProjection = formData?.viewProfile?.data?.data?.uv_projection || 'cylindrical'

			// Store on threeViewer for the shader to read
			;(window as any).threeViewer.uvProjection = uvProjection

			// Which section model this profile runs on. Resolved through the shared helper so
			// the Customizer reaches the same answer for the same profile — see
			// shared/sectionFrames/sectionModel.ts. Absent means cylindrical, so every profile
			// that exists today keeps the behaviour it has.
			const sections = (formData?.sections ?? [])
				.map((s: any) => s?.formData)
				.filter(Boolean)
				.map((s: any) => ({ target_object: s.targetObject }))
			;(window as any).threeViewer.sectionModel = resolveSectionModel(
				formData?.viewProfile?.data?.data,
				sections
			)

			await loadProduct(base64, uvProjection)

			// Apply the profile's material preset, exactly as the Customizer does.
			//
			// This is the single largest reason an operator's preview and a shopper's view
			// have never matched: env_param.model_material (steel, glass, acrylic) REPLACES
			// every mesh's material, and only the Customizer implemented it. A profile using
			// a preset previewed as the raw GLB here and as the preset there — a difference
			// no amount of keeping the two codebases in step could have closed, because only
			// one of them had the feature.
			//
			// Before the overlays are drawn below: the preset swaps mesh.material, which
			// would otherwise orphan an overlay already installed on the old one.
			applyProductEnvMaterials((window as any).threeViewer?.product, formData?.lights)
			if (formData?.sections?.length > 0) {
				const drawingData = formData.sections.map((section: any) => section.formData).filter((s: any) => s.targetObject)
				// Select the first section and only draw that one
				const firstSection = formData.sections[0]?.formData
				if (firstSection) {
					setSelectedSection(firstSection)
					setOpenForm('section')
					updateAllTargetObjects(drawingData, firstSection.sectionId)
					animateTo(firstSection.radialAngle, 0.005)
				}
			}
			setLoaded(true)
			setLoading(false)
		}
	}



	const handleAddSection = () => {
		const proj = (window as any).threeViewer?.uvProjection
		const isPlanarOrBox = proj === 'planar' || proj === 'box'

		const newSection = {
			sectionId: uuidv4(),
			name: `Section ${(sectionList?.length || 1) + 1}`,
			targetObject: sectionList?.length > 0 ? sectionList[sectionList?.length - 1]?.formData?.targetObject || null : null,
			radialAngle: 0,
			centerRadialAngle: 0,
			...(isPlanarOrBox ? { face: { value: 'front', label: 'Front' }, offsetX: '0' } : {}),
		}

		const updatedSections = [...(sectionList || []), {
			formData: newSection,
			validations: {},
		}]

		setSectionList(updatedSections)
		setSelectedSection(newSection)
		setOpenForm('section')

		// Clear all overlays when adding a new section so user can see plain model
		cleanAllTargetObjects()
	}

	const handleSelectSection = async (sectionId: string) => {
		const section = sectionList.find((s) => s.formData?.sectionId === sectionId)
		setSelectedSection(section.formData)
		// Frame sections are framed by their own normal, the way the Customizer does it when
		// a shopper picks a section. radialAngle describes a band around an axis and is
		// always 0 on a frame, so the cylindrical path would swing the product to its front
		// whatever face the section is on.
		const framed = await zoomToFrameSection(section.formData)
		if (!framed) animateTo(section.formData.radialAngle, 0.005)
		setOpenForm('section')
		// Draw only the selected section
		const drawingData = sectionList.map((s) => s.formData).filter(s => s.targetObject && s.width && s.height)
		if (drawingData.length > 0) {
			updateAllTargetObjects(drawingData, sectionId)
		}
	}

	const handleOpenLightingForm = () => {
		setOpenForm('lighting')
		setSelectedSection(null)
	}

	const handleCloseForm = () => {
		setOpenForm(null)
		setSelectedSection(null)
	}

	const handleUpdateSections = ({ formData, validations, isDeleted }: { formData: any, validations: any, isDeleted: boolean }) => {

		if (!selectedSection) return
		let updatedSections = sectionList
		if (!isDeleted) {
			updatedSections = sectionList.map((section) =>
				section.formData.sectionId === formData.sectionId ?
					{ formData, validations } : section)
		} else {
			updatedSections = updatedSections.filter((s) => s.formData.sectionId !== formData.sectionId)
			setOpenForm(null)
		}

		setSectionList(updatedSections)
		props?.onChange?.('sections', updatedSections)

		// Only redraw overlays if the current section has width and height
		// (skip redraw for newly added sections without dimensions to keep overlays cleared)
		if (formData.width && formData.height) {
			const drawingData = updatedSections.map((section) => section.formData).filter(s => s.targetObject && s.width && s.height)
			// Debounce the expensive 3D update to avoid shader recompilation on every slider tick
			debouncedUpdateAllTargetObjects(drawingData, formData.sectionId)
		}

	}

	const handleUpdateLights = (formData: any) => {
		setLights(formData)
		// Material settings live in env_param alongside the light intensities, so a change
		// here can mean a different preset, not just a different exposure.
		applyProductEnvMaterials((window as any).threeViewer?.product, formData)
		props?.onChange?.('lights', formData)
	}



	const handleFullScreen = () => {
		const el = document.getElementById('section-3d-plugin')
		if (!el) return
		if (fullScreen) {
			el.removeAttribute('style')
			setFullScreen(false)
		} else {
			el.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;z-index:9999;'
			setFullScreen(true)
		}
		(window as any).threeViewer.updateSize()
	}



	useEffect(() => {
		const newSections = formData.sections || []
		setSectionList(newSections)

		// When sections change (e.g. profile switch), auto-select the first section
		// if the currently selected section no longer exists in the new list
		if (loaded && newSections.length > 0) {
			const stillExists = selectedSection && newSections.some((s: any) => s.formData?.sectionId === selectedSection.sectionId)
			if (!stillExists) {
				const firstSection = newSections[0]?.formData
				if (firstSection) {
					setSelectedSection(firstSection)
					setOpenForm('section')
					const drawingData = newSections.map((s: any) => s.formData).filter((s: any) => s.targetObject)
					updateAllTargetObjects(drawingData, firstSection.sectionId)
					animateTo(firstSection.radialAngle, 0.005)
				}
			}
		}
	}, [formData.sections, loaded])



	useEffect(() => {

		const glbLoaded = formData?.glb?.loaded
		if (!file && formData.glb.string && initialized && !loading && glbLoaded) {
			setLoading(true)
			setFile(formData.glb.string)
			loadProductIntoScene(formData.glb.string)
		} else if (file !== formData.glb.string && initialized && !loading && glbLoaded) {
			setLoading(true)
			setFile(formData.glb.string)
			loadProductIntoScene(formData.glb.string)
		}
		if (formData.lights && loaded && JSON.stringify(formData.lights) !== JSON.stringify(lights)) {
			setLights(formData.lights)
		}
	}, [formData, file, loaded, initialized, loading, loaded])

	useEffect(() => {
		setInitialized(false)
		setSelectedSection(null)
		setLoaded(false)
		setLoading(false)
		initThreeViewer()


		return () => {
			if (updateDebounceRef.current) clearTimeout(updateDebounceRef.current)
			;(window as any).threeViewer = null
		};
	}, [])



	const designSection = (
		<div className="w-full rounded-md relative bg-gray-100 shadow border border-gray-300 p-0 flex-row flex h-[calc(100vh-100px)] overflow-hidden" id={'section-3d-plugin'}>
			<Splash text={'Loading 3D Preview...'} className={'opacity-100'} isOpen={!loaded} />
			<div className="flex !item-center flex-col gap-lg max-w-[132px] w-max  z-[1] p-md">
				<div className={`flex w-[100px] ${sectionList?.length > 0 ? 'justify-center' : ' justify-start'}`}>
					<Button onClick={handleAddSection} className="!rounded-full font-icon-solid w-[44px] h-[44px]">+</Button>

				</div>
				<div className="flex justify-center inset-1 flex-1">
					{sectionList?.length > 0 && (
						<div className={'flex flex-col gap-sm '}>
							<ButtonList
								className={(selected, item) =>
									`w-[100px] h-[100px] bg-white rounded-md border border-gray-200 justify-center items-center overflow-hidden hover:border-primary-500 active:shadow-focus 
                                    ${selected ? `shadow-xl border-primary-500  border-[2px] ` : ''}
                                    ${item?.validation > 0 ? 'border-red-500 hover:border-red-500 active:shadow-red' : ''}
                                    `
								}
								items={sectionList?.map((section: any, index: number) => {
									const item = section.formData
									const validations = Object.values(section.validations || {})

									return ({
										...item,
										value: item.sectionId,
										label: (
											<div className={'flex justify-center items-center h-[100%] w-[100%] relative p-xs'}>
												{sectionList[index].formData.targetObject ? <canvas id={`targetObjectCanvas-${sectionList[index].formData.targetObject.name}`} width={'100%'} className={'border border-gray-300 rounded-md bg-white shadow-xs hidden'} /> : null}
												{validations?.length > 0 && (
													<div
														className={'bg-red-500 text-white text-[11px] absolute bottom-0 left-1/2 -translate-x-1/2  w-[100%] flex justify-center items-center'}
													>
														{validations?.length}
													</div>
												)}
												{
													sectionList[index].formData?.thumbnail
														?
														<img src={sectionList[index].formData.thumbnail.string} className={'object-contain h-[100%]'} alt={item.name} />
														:

														<span className={'flex flex-col '}>
															<span className={'font-icon-regular text-[30px] text-gray-300'}></span>
															<span className={'text-title-sm text-gray-500'}>{item.name}</span>
														</span>
												}

											</div>
										),
										index: index,
										validation: validations.length,
									})
								})}
								selected={selectedSection?.sectionId}
								onClick={(val) => {
									handleSelectSection(val)
								}}
							/>
						</div>
					)}

				</div>
				<div className={`flex flex-col gap-md  ${sectionList?.length > 0 ? 'items-center' : ' items-start'}`}>
					<Button onClick={handleOpenLightingForm} className="!rounded-full font-icon-solid w-[44px] h-[44px] "></Button>
					<Button onClick={handleFullScreen} type={'alt'} className="!rounded-full font-icon-solid w-[44px] h-[44px] ">{fullScreen ? '' : ''}</Button>

				</div>
			</div>


			<div className="flex flex-1 z-[1] overflow-hidden">
				<div
					ref={containerRef}
					id={"threeViewerDiv"}
					style={{ width: '100%', height: '100%', outline: 'none' }}
				/>
			</div>


			<style>{`canvas{width:100%!important}`}</style>


			<div className="z-[1] relative !w-[382px] h-full shrink-0 rounded-md">
				<div
					ref={sectionFormScrollRef}
					className="relative w-full h-full justify-end pb-md rounded-md p-md block"
					style={{ overflowY: 'hidden', overflowX: 'hidden' }}
				>
					<SectionForm
						profileType={formData?.profileType?.value || formData?.profileType}
						isOpen={openForm === 'section'}
						close={handleCloseForm}
						onUpdate={handleUpdateSections}
						sectionData={selectedSection}
						sectionList={sectionList}

					/>

					<LightingForm
						isOpen={openForm === 'lighting'}
						close={handleCloseForm}
						onUpdate={handleUpdateLights}
						lights={lights}
					/>
				</div>
				<ScrollToBottomButton containerRef={sectionFormScrollRef} />

			</div>
		</div>
	)

	return (
		<StickyDesignSection scrollContainerRef={sectionFormScrollRef} disabled={fullScreen}>
			{designSection}
		</StickyDesignSection>
	)
}

export default memo(Preview3dPlugin)
